/**
 * THE SELF-MODEL CONTRACT (Nami Phase 4 Train 1, spec §2).
 *
 * Six fixed pages about the user, under one seed folder. Goals and Projects have a
 * fixed set of `## ` sections; the other four are articles with headings of their own
 * (see SELF_PAGES). The ORDER here is the order everywhere: the About-you view, the
 * kernel compile, the dream's pass message. The shape is ours; the words are his.
 *
 * A self page states no frontmatter key beyond the spine except its one-line
 * `description` (brain articles, Task 4 review): the stable facts about the user live
 * in About me's facts card, in the body, where the gate's address rule reads them.
 *
 * PURE AND BROWSER-SAFE, like brain-worlds: the web reads it to find the six on the
 * tree and to name a Recent-updates row.
 */
import { KNOWLEDGE_DIR, MEMORY_ROOT, SELF_SEED_FOLDER } from './memory-tree.js';

/** The `type:` every self page declares. */
export const SELF_TYPE = 'self';

/** Where the six live, tree-relative: `knowledge/self`. */
export const SELF_FOLDER = `${KNOWLEDGE_DIR}/${SELF_SEED_FOLDER}`;

/**
 * The six (brain articles, Task 5b: principles, not templates).
 *
 * THE FOUR PROSE PAGES HAVE NO FIXED SECTIONS (`sections: null`). Who you are, How you
 * communicate, Preferences and How you work are articles in the second person: a lead,
 * then headings the dream names for what this user's evidence shows. `covers` says what
 * each page is about, and the charter's roster prints it after the title, so it never
 * repeats the title. GOALS AND PROJECTS KEEP
 * THEIRS: Today / Two weeks / Two months / Retired and Active / Blocked / Retired are
 * workflow states, not article headings.
 *
 * THE LEGACY TITLES ARE STILL READ, NEVER WRITTEN. A manifest compiled before the titles
 * changed still says `## About me`, and the readers map the legacy title. No page keeps a
 * legacy SECTION set: the four accept any headings, and Goals and Projects never changed.
 */
export const SELF_PAGES = [
  {
    slug: 'about-me',
    title: 'Who you are',
    legacyTitles: ['About me'],
    covers: 'your work, your company, the people around you',
    sections: null,
  },
  {
    slug: 'how-i-communicate',
    title: 'How you communicate',
    legacyTitles: ['How I communicate'],
    covers: 'your voice with each audience, your phrases and sign-offs',
    sections: null,
  },
  {
    slug: 'preferences',
    title: 'Preferences',
    legacyTitles: [],
    covers: 'how you like things done, and what you never want',
    sections: null,
  },
  {
    slug: 'work-patterns',
    title: 'How you work',
    legacyTitles: ['Work patterns'],
    covers: 'your rhythm, your tools and channels, your habits',
    sections: null,
  },
  {
    slug: 'goals',
    title: 'Goals',
    legacyTitles: [],
    covers: 'what you are aiming at, by horizon',
    sections: ['Today', 'Two weeks', 'Two months', 'Retired'],
  },
  {
    slug: 'projects',
    title: 'Projects',
    legacyTitles: [],
    covers: 'what you are building, and where each one stands',
    sections: ['Active', 'Blocked', 'Retired'],
  },
] as const;

export type SelfPage = (typeof SELF_PAGES)[number];
export type SelfSlug = SelfPage['slug'];

/** A dream line the brain concluded from a pattern carries this before its citation. */
export const SELF_INFERRED_MARK = '(inferred)';

/**
 * A page path with the tree root taken off — the one normalisation the two readers
 * below share (P4-R10).
 *
 * THIS CODEBASE HOLDS A PAGE PATH IN TWO SPELLINGS. The listing, `pagePathRe` and the
 * wire all speak the tree-relative one (`knowledge/self/goals.md`); anything composed
 * off `TREE.knowledge.root` carries the tree root in front of it, which is the form
 * `evidenceStubPath` and every `TREE` constant hand out. An exact comparison against
 * one of them answers "not a self page" about a self page written the other way —
 * silently, and type-cleanly. `pageTypeOf` is tolerant for exactly this reason; so is
 * this. (An absolute workspace path is neither spelling and is not accepted; callers
 * pass the path as the tree or the listing spells it.)
 *
 * SPELLED THROUGH THE CONSTANT, never a literal: the memory-path ratchet is what keeps
 * the tree's root in one place, and a hand-typed prefix here would be a second one.
 *
 * ONE PREFIX, NOT A SCRUBBER. Everything below the root still has to match a self page
 * exactly, so a seventh file in the folder and a page nested under it are both refused.
 */
const belowTreeRoot = (path: string): string =>
  path.startsWith(`${MEMORY_ROOT}/`) ? path.slice(MEMORY_ROOT.length + 1) : path;

/** Where a self page is filed, TREE-RELATIVE — the spelling `pagePathRe` admits and the
 *  listing serves. Prefix it with `MEMORY_ROOT` for the absolute form. */
export const selfPagePath = (slug: string): string => `${SELF_FOLDER}/${slug}.md`;

/** Is this path one of the six? Reads either spelling (`belowTreeRoot`), and only the
 *  six exactly: not a seventh file in the folder, not a page nested below it. */
export const isSelfPagePath = (rel: string): boolean =>
  SELF_PAGES.some((p) => selfPagePath(p.slug) === belowTreeRoot(rel));

/** Which of the six this path is, and `null` for everything else. Either spelling. */
export const selfSlugOf = (rel: string): SelfSlug | null =>
  SELF_PAGES.find((p) => selfPagePath(p.slug) === belowTreeRoot(rel))?.slug ?? null;
/** A page's fixed `## ` sections; `null` for one of the four free pages, or a slug that is not one of the six. */
export const selfSectionsOf = (slug: string): readonly string[] | null =>
  SELF_PAGES.find((p) => p.slug === slug)?.sections ?? null;
/** What a page covers, one line, as the charter's roster says it; `null` for a slug that is not one of the six. */
export const selfCoversOf = (slug: string): string | null => SELF_PAGES.find((p) => p.slug === slug)?.covers ?? null;
export const selfTitleOf = (slug: string): string | null => SELF_PAGES.find((p) => p.slug === slug)?.title ?? null;

/** The slug a compiled `## <Title>` names, reading the current title or a legacy one. */
export const selfSlugOfTitle = (title: string): SelfSlug | null =>
  SELF_PAGES.find((p) => p.title === title || (p.legacyTitles as readonly string[]).includes(title))?.slug ?? null;

/**
 * One line of the brain home's Recent-updates block (spec §6): derived from git at
 * publish, newest first, at most SELF_UPDATES_MAX (nami-self-manifest).
 */
export interface SelfUpdate {
  /** Commit time, ISO. */
  at: string;
  /** The page's slug, e.g. `goals`. */
  page: string;
  /** The `## ` heading the line sits under: '' for the lead above the first heading, and
   *  for a commit that only removed lines from a page that has none. */
  section: string;
  /** The added line, markdown, cites intact, ≤ 160 chars cut at a word; or `retired N line(s)`. */
  line: string;
  /** `GIT_AUTHORS.user` vs anything else. */
  by: 'you' | 'brain';
}
